import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { nl2ParagraphsFilter } from 'components/sharpr/nl2br/nl2brFilter';
import spinner from 'components/sharpr/spinner/SpinnerSingleton';
import { ensure } from 'components/sharpr/ensure/ensure';
import moment from 'moment';
import _ from 'lodash';
import { spliceOut } from 'components/sharpr/util/spliceOut';
import { PostTypes } from '../postType/PostTypesEnum';
import stripTags from '../../../../../a-react-app/src/libs/stripTags/stripTags.js';
import pluralize from '../../../../../a-react-app/src/libs/pluralize/pluralize.js';
import getMetadata from '../../../../../a-react-app/src/_common/libs/fileMetadata/ExtractFileMetadataProperties.js';
import dayjs from 'dayjs';
import generatePostHeadline from '../../../../../a-react-app/src/_common/libs/openAI/generatePostHeadline.js';
import PostAction from '../../../../../a-react-app/src/_common/domain/enums/PostAction.js';

export class PostEditBaseCtrl {
	// Classes that inherit from this class must save the following injections:
	// Identity, Api, Taxonomy, Theme, Cfg, AutoArchive, FileDrag, $q, $timeout, and $interval
	constructor(Cfg, Api, Identity) {
		'ngInject';

		this.Cfg = Cfg;
		this.Api = Api;
		this.Identity = Identity;

		// bind some functions to itself
		this.tagProvider = this.tagProvider.bind(this);
		this.suggestUser = this.suggestUser.bind(this);
		this._handleVisualTags = this._handleVisualTags.bind(this);
		this._handleCategories = this._handleCategories.bind(this);
		this._handleContributors = this._handleContributors.bind(this);
		this._handleClassifications = this._handleClassifications.bind(this);
		this._handleTaxonomies = this._handleTaxonomies.bind(this);
		this._handleDefaultArchivePeriods = this._handleDefaultArchivePeriods.bind(this);
		this.runImageUpdate = this.runImageUpdate.bind(this);
		this.isAccessRulesFeatureEnabled = this.isAccessRulesFeatureEnabled.bind(this);
		this.isAccessRuleRequired = this.isAccessRuleRequired.bind(this);
		this.validate = this.validate.bind(this);
		this.validatePost = this.validatePost.bind(this);
		// setup mentionable
		this.mentionable = this.getMentionable();
		// initialize to correct section or main if no query param was passed
		// allows us to iframe the post edit screen and go to a specific tab
		const sectionMatch = window.location.href.match(/[?&]section=(\w+)/);
		if (sectionMatch) {
			this.section = sectionMatch[1];
		} else {
			this.section = 'main';
		}
		// create some empty vars
		this.uploadState = null;
		this.scrollBySection = {};
		this.deregisterListeners = {};
		this.filesWithDuplicates = [];
		this.isPublished = false;
		this.storyIds = null; // NOTE: This may be set to an array in method showInsightBriefChoices.
		this.allowedExtensions = [];
		this.accessRules = [];

		this.canAddToInsightBrief = true;

		// Create a holder for the modal reference, which may or may not be injected into the child class.
		this.modal = null;
	}

	/**
	 * Updates the "access rule" and "access rule ID" on the post object.
	 *
	 * If the `rule` is `null`, the "access rule" and "access rule ID" are set to null.
	 *
	 * @param {Object|null} rule - The access rule object, or null if no rule is selected.
	 * @param {number} [rule.id] - The ID of the access rule. Only present if rule is an object.
	 * @param {string} [rule.name] - The name of the access rule. Only present if rule is an object.
	 * @param {string} [rule.description] - A description of the access rule. Only present if rule is an object.
	 * @param {string} [rule.icon] - The icon for the access rule. Only present if rule is an object.
	 * @param {string} [rule.color] - The color for the access rule. Only present if rule is an object.
	 * @param {boolean} [rule.isSingleUse] - Whether the access rule is single use. Only present if rule is an object.
	 * @param {Object[]} [rule.actions] - An array of action objects associated with the rule. Only present if rule is an object.
	 * @param {Object[]} [rule.roles] - An array of role objects associated with the rule. Only present if rule is an object.
	 * @param {Object[]} [rule.groups] - An array of group objects associated with the rule. Only present if rule is an object.
	 * @param {Object[]} [rule.hubs] - An array of hub objects associated with the rule. Only present if rule is an object.
	 * @returns {void}
	 */
	handleAccessRuleChange = rule => {
		// When no access rule is selected, rule = NULL
		this.canAddToInsightBrief =
			rule === null || rule?.actions === null || rule?.actions?.includes(PostAction.AddToInsightBrief);
		this.post.access_rule = rule;
		this.post.access_rule_id = rule?.id || null;
	};

	async setup() {
		this._setupViewPreferences();
		const accountId = this.Identity.currentAffiliationId();
		this.accessRulesAdminPageUrl = `${window.BASE_URL}/core/accounts/${accountId}/access_rules`;
		// If it is greater than 600px on load, the nav shows. Otherwise, it's hidden but shows when the user clicks the hamburger icon.
		// `600` matches the media query in post-edit.css
		this.showScreenNavigation = window.innerWidth > 600;
	}

	// meant to hold variables and read preference values used to handle UI changes
	_setupViewPreferences() {
		let insight = this.Cfg.read('Ui.max_chars_insight');
		if (insight === '0' || insight === 0) {
			// zero means hide insight bubble
			insight = 0;
		} else {
			insight = parseInt(insight) || 500;
		}
		const headline = parseInt(this.Cfg.read('Ui.max_chars_headline')) || 161;
		const body = parseInt(this.Cfg.read('Ui.max_chars_body')) || 65355;
		this.maxChars = { insight, headline, body };
		const tagsTabVisibilityPreference = this.Cfg.read('Ui.post_editor_tags_tab');
		this.shouldShowTagsTab = tagsTabVisibilityPreference === '1';
		this.accountId = this.Identity.currentAccountId();
		this.skipDuplicatesCheck = this.Cfg.readAsBool('DocProcessing.check_for_duplicates') === false;
		this.showSourceTab = this.Cfg.readAsBool('UI.post_show_source_tab');
		this.showShareUrl = this.Cfg.readAsBool('UI.post_show_share_url');
		this.showInsightsBubble = this.Cfg.readAsBool('UI.post_show_insights_bubble');
		this.showTagsInputBox = this.Cfg.readAsBool('UI.post_show_tags_box');
	}

	loadNewTaxonomyAndTheme() {
		this.$q.all([this.loadTaxonomy(), this.Theme.load()]);
	}

	_handleVisualTags(data) {
		this.visualTagList = ensure.array(data);
	}

	_handleCategories(data) {
		const categories = ensure.array(data);
		this.hasCategories = categories.length > 0;
		if (this.Cfg.readAsBool('Features.add_category_create_post_screen')) {
			categories.push({ id: -1, name: '+ Add new...' });
		}
		categories.unshift({ id: 0, name: 'Uncategorized' });
		if (this.mode === 'bookmarklet' || this.mode === 'create') {
			categories.unshift({ id: '', name: 'Category...' });
		}
		this.categories = categories;
	}

	_handleContributors(data) {
		this.topContributors = ensure.array(data).map(user => {
			return {
				id: user.id,
				$label: `${user.fname_preferred || user.fname} ${user.lname_preferred || user.lname}`,
			};
		});
	}

	_handleClassifications(data) {
		this.classifications = ensure.array(data);
		if (this.classification) {
			const chosenCls = _.find(this.classifications, { id: this.classification.id });
			if (chosenCls) {
				chosenCls.selected = true;
			}
		}
		if (this.classifications.length > 0) {
			// select the first classification/status if this is a brand new RM post (PLA-3490)
			if (this.isResearchManagementHub && (!this.post || !this.post.id)) {
				// the API returns classifications in the order defined in `classifications.sort`, so we can count on [0] being the one they want auto selected.
				this.classifications[0].selected = true;
				this.chooseClassification(this.classifications[0]);
			}
			return AssetManager.css('/assets/fonts/Linearicons/style.css');
		}
	}

	_handleDefaultArchivePeriods() {
		const archivePreset = this.AutoArchive.getArchiveDropdown();

		if (this.mode === 'edit') {
			if (this.post.archive_selection === 'current') {
				const archiveLabel = this.AutoArchive.getArchiveSelectionLabel(this.post.archive_action, this.post.archive_at);
				archivePreset.unshift({
					id: this.post.archive_selection,
					name: `${archiveLabel} (current)`,
				});
			} else {
				this.post.archive_selection = 'never';
			}
		} else {
			this.post.archive_selection = this.Cfg.read('PostArchive.default_period');
		}
		this.archivePreset = archivePreset;
	}

	setupLabels() {
		this.labels = {
			classification: this.isResearchManagementHub ? 'Status' : 'Classification',
			post: this.isResearchManagementHub ? 'Project' : 'Post',
			insightBy: this.isResearchManagementHub ? 'Project Manager' : 'Insight By',
			submittedBy: this.isResearchManagementHub ? 'Created By' : 'Submitted By',
			taxonomies: this.Cfg.read('Text.taxonomies_name') || 'Filters',
			accessRules: 'Access',
			files: this._setupFilesTab(),
		};
	}

	_setupFilesTab() {
		// Should always be either "Files" or "Attachments"
		const label = this.Cfg.read('UI.post_files_tab_name');
		// Use some regex to strip the last character of the string to give us the singular version
		const singular = `1 ${label.replace(/.$/, '')}`;
		const count = Number(this.attachments.length);
		const fileTabLabel = {
			count,
			when: {
				0: label,
				1: singular,
				other: `{} ${label}`,
			},
		};
		return pluralize(fileTabLabel);
	}

	_handleTaxonomies(data) {
		this.taxonomies = data;
		this.originalTaxonomies = {};
		this.updatedTaxonomies = {};
		// get a list of ids representing the selected options at load time. This is later compared to
		/// to updates to decide if DB updates are necessary.
		Object.keys(this.taxonomies).forEach(taxonomyName => {
			this.originalTaxonomies[taxonomyName] = [];
			this.taxonomies[taxonomyName].options.forEach(option => {
				if (option.selected === true) {
					this.originalTaxonomies[taxonomyName].push(parseInt(option.taxonomy_option_id));
				}
			});
		});
	}
	/**
	 * Not intended for bookmarklet or SHARP IT
	 */
	setupResearchManagement() {
		this.isResearchManagementHub = this.Cfg.readAsBool('ResearchManagement.enabled');
	}

	showTaxonomies(taxonomies) {
		const showTaxonomiesPreference = this.Cfg.readAsBool('Taxonomies.show_tab_on_post_edit');
		if (showTaxonomiesPreference && taxonomies) {
			return Object.keys(taxonomies).length > 0;
		} else {
			return false;
		}
	}

	loadTaxonomy() {
		const hubId = this.Identity.currentClient().id;
		this.isTaxononmyLoading = true;
		return this.Cfg.load(hubId).then(() => {
			let taxonomiesEndpoint = '/taxonomies';
			if (this.post && this.post.id) {
				taxonomiesEndpoint = `/posts/${this.post.id}/taxonomies`;
			}
			const requests = [
				this.Api.get('/categories', {}, { avoidLoadingBar: true }).then(this._handleCategories),
				this.Api.get('/users/top_contributors', { limit: 10 }, { avoidLoadingBar: true }).then(this._handleContributors),
				this.Api.get('/classifications', {}, { avoidLoadingBar: true }).then(this._handleClassifications),
				this.Api.get('/config/search?code=SharpIt.required_fields', {}, { avoidLoadingBar: true }).then(
					fields => (this.requiredFields = fields)
				),
				this.Api.get(taxonomiesEndpoint, {}, { avoidLoadingBar: true }).then(this._handleTaxonomies),
			];
			if (this.Cfg.read('Features.visual_tags') === '1') {
				requests.push(this.Api.get('/visual_tags', {}, { avoidLoadingBar: true }).then(this._handleVisualTags));
				this.visualTagsLabel = this.Cfg.read('Features.visual_tags_label');
			} else {
				this.visualTagsLabel = null;
				this._handleVisualTags([]);
			}

			if (this.Cfg.readAsBool('PostArchive.enable_archive') || this.Cfg.readAsBool('PostArchive.enable_destroy')) {
				this.postArchiveEnabled = true;
				this._handleDefaultArchivePeriods();
			} else {
				this.postArchiveEnabled = false;
			}
			return this.$q.all(requests).then(() => (this.isTaxononmyLoading = false));
		});
	}

	isUploading() {
		return this.uploadState == 'uploading';
	}

	pleaseWaitForUpload() {
		spinner.stopAll();
		this.section = 'attachments';
		this._alert('Please wait until all attachments finish uploading.');
		return Promise.reject();
	}

	_alert(msg) {
		if (this.modal) {
			return this.modal.alert({ escapedHtml: msg.replace(/\n/g, '<br>') });
		}
		alert(msg);
		return this.$q.resolve();
	}

	_prompt(msg) {
		if (this.modal) {
			return this.modal.prompt({ text: msg });
		}
		const value = prompt(msg);
		if (value) {
			return this.$q.resolve(value);
		}
		return this.$q.reject();
	}

	chooseClassification(cls) {
		this.section = 'main';
		this.classification = cls;
	}

	gotoSection(section) {
		const editArea = document.querySelector('.post-add-edit .edit-area-container');
		if (editArea) {
			this.scrollBySection[this.section] = editArea.scrollTop;
			editArea.scrollTop = this.scrollBySection[section] || 0;
		}
		this.section = section;

		// `600` matches the media query in post-edit.css
		if (window.innerWidth < 600) {
			// when in smaller windows, always hide the menu after changing sections
			this.showScreenNavigation = false;
		}
	}

	exitSection(section) {
		this.gotoSection('main');
	}

	prepareTagTabs() {
		this.aiTagSort = this.aiTagSort || '-relevance';
		this.vocabTagSort = this.vocabTagSort || 'keyword';
		this.manualTagSort = this.manualTagSort || 'name';
		this.aiTags = ensure.array(this.keywords).filter(kw => kw.type === 'ai');
		this.vocabTags = ensure.array(this.keywords).filter(kw => kw.type === 'vocab');
		this.manualTags = ensure.array(this.post.tags).filter(tag => !tag.is_visual);
	}

	addManualTag() {
		if (this.newTagName === '') {
			return;
		}
		// <sh-typeahead-multi> looks at tag.$search so we need to set it
		const newTag = {
			name: this.newTagName,
			$search: this.newTagName,
		};
		this.post.tags.push(newTag);
		this.manualTags.push(newTag);
		this.newTagName = '';
	}

	removeManualTag(tag) {
		spliceOut(this.post.tags, tag);
		spliceOut(this.manualTags, tag);
	}

	updateManualTagName(tag) {
		// <sh-typeahead-multi> looks at tag.$search so we need to update it
		tag.$search = tag.name;
	}

	maybeAddCategory(oldValue) {
		if (this.post.category.id != '-1') {
			// chose existing category
			return;
		}
		this._prompt('Name of new category:').then(
			newCategory => {
				if (!newCategory) {
					// canceled
					return;
				}
				this.Api.create('/categories', {
					name: newCategory,
				}).then(categoryId => {
					const newObj = {
						id: categoryId,
						name: newCategory,
					};
					// add the new item right before "+ Add new"
					this.categories.splice(this.categories.length - 1, 0, newObj);
					// make a copy of categories to trigger <sh-select> component change
					this.categories = this.categories.slice(0);
					this.post.category.id = categoryId;
				});
			},
			() => {
				this.post.category.id = oldValue;
			}
		);
	}

	tagProvider(term) {
		if (this.visualTagList.length > 0 && term.match(/^(mega_|macro_)$/)) {
			return Promise.resolve([]);
		}
		const limit = 10;
		const not_names = ensure
			.array(this.post.tags)
			.map(t => t.name)
			.filter(Boolean)
			.join('|');
		return this.Api.get('/tags/search', { term, limit, not_names }, { avoidLoadingBar: true }).then(tags => {
			return ensure.array(tags).map(tag => {
				return {
					...tag,
					$search: tag.name,
				};
			});
		});
	}

	suggestUser(term) {
		const limit = 10;
		return this.Api.get(
			'/users/search',
			{
				term,
				limit,
				role: '!observer',
			},
			{ avoidLoadingBar: true }
		).then(users => {
			return ensure.array(users).map(user => {
				return {
					id: user.id,
					$label: `${user.fname_preferred || user.fname} ${user.lname_preferred || user.lname}`,
				};
			});
		});
	}

	getMentionable() {
		const mentionable = {
			notifications: [],
			removeNotification: notif => {
				const idx = mentionable.notifications.indexOf(notif);
				if (idx > -1) {
					mentionable.notifications.splice(idx, 1);
				}
			},
			handlers: [
				{
					match: /^@[a-z\u00C0-\u017F]*/i, // At sign followed by letters or international letters
					provider: word => {
						return this.Api.get(
							'/users/search',
							{
								term: word.slice(1),
								role: 'intel leader',
								limit: 10,
							},
							{ avoidLoadingBar: true }
						).then(users => {
							return ensure.array(users).map(user => {
								const firstName = user.fname_preferred || user.fname;
								const lastName = user.lname_preferred || user.lname;

								return {
									id: user.id,
									type: 'user',
									fullname: `${firstName} ${lastName}`,
									$suggest: `${firstName} ${lastName} <${user.email}>`,
									$replace: `@${firstName}${lastName}`.replace(/\s/g, ''),
								};
							});
						});
					},
				},
				{
					match: /^#[\da-z\u00C0-\u017F]*/i, // Hash sign plus numbers, letters and international letters
					provider: word => {
						return this.Api.get(
							'/tags/search',
							{
								term: word.slice(1),
								limit: 10,
							},
							{ avoidLoadingBar: true }
						).then(tags => {
							return ensure.array(tags).map(tag => {
								return {
									...tag,
									type: 'tag',
									$suggest: '#' + tag.name,
									$replace: '#' + tag.name,
								};
							});
						});
					},
				},
			],
			afterReplace: ({ item }) => {
				if (item.type == 'user') {
					const user = item;
					if (mentionable.notifications.some(u => u.id == user.id)) {
						// don't add someone twice
						return;
					}
					mentionable.notifications.push({
						id: user.id,
						fullname: user.fullname,
					});
				} else if (item.type == 'tag') {
					const tag = item;
					ensure.array(this.post.tags).push({
						...tag,
						$label: tag.name,
					});
				}
			},
		};
		return mentionable;
	}

	resetPost() {
		let post = {
			id: 0,
			category: { id: '' },
			submitted_by: {},
			published_by: {},
			published_at: moment(),
			source_date: moment(),
			intel_text: this.Cfg.read('Ui.default_bubble_text') || '',
			rich_text_body: this.Cfg.read('Ui.default_body_text') || '',
			headline: '',
			tags: [],
			archive_selection: '',
			access_rule_id: null,
		};
		// dependent data
		this.originUrl = null;
		this.image = false;
		this.attachments = [];
		this.chosenFileIds = [];
		this.classification = false;
		if (this.mode == 'bookmarklet' || this.mode == 'create' || this.mode == 'external') {
			const user = this.Identity.user();
			post.submitted_by = post.published_by = {
				id: user.id,
				$label: `${user.fname_preferred || user.fname} ${user.lname_preferred || user.lname}`,
			};
		}

		this.post = post;
		return post;
	}

	unpickFile(file) {
		if (file.id) {
			const idx = this.chosenFileIds.indexOf(file.id);
			if (idx >= 0) {
				this.chosenFileIds.splice(idx, 1);
			}
			if (!file.created_at) {
				// this file was uploaded this time and is not yet connected to the post
				this.Api.delete(`/attachments/${file.id}`);
			}
		}
	}

	removeImage() {
		this.image = false;
	}

	fetchUrl() {
		const params = { article_url: this.originUrl };
		this.isFetching = true;
		this.startFetchMessageCycle();
		return this.Api.get('/posts/scrape', params).then(
			post => {
				post = ensure.object(post);
				this.isFetching = false;
				this.stopFetchMessageCycle();
				const scalarFields = ['headline', 'source_link', 'source_name', 'source_author'];
				scalarFields.forEach(field => (this.post[field] = post[field]));
				const diffbotType = this.Cfg.read('Features.bookmarklet_diffbot');
				if (diffbotType == 'full_text') {
					this.post.rich_text_body = post.rich_text_body_basic || '';
				} else {
					// summary or metatags or invalid value
					this.post.rich_text_body = nl2ParagraphsFilter()(post.summary) || post.rich_text_body_basic || '';
				}
				this.post.full_text = post.body || '';
				this.post.source_date = moment(post.source_date || undefined);
				this.keywords = post.keywords || [];
				this.post.summary = post.summary;
				if (post.image) {
					this.image = post.image;
				}
			},
			() => {
				this.stopFetchMessageCycle();
				this.fetchMessageText = 'Error fetching post. Please try another URL.';
			}
		);
	}

	startFetchMessageCycle() {
		let idx = 0;
		const messages = [
			'Fetching post data...',
			'Fetching image data...',
			'Processing post...',
			'Processing image...',
			'Formatting data...',
			'Still working...',
			'Please wait...',
		];
		const updateMessage = () => {
			if (idx >= messages.length) {
				return;
			}
			this.fetchMessageText = messages[idx];
			idx++;
		};
		updateMessage();
		this._cyclerPromise = this.$interval(updateMessage, 2000);
	}

	stopFetchMessageCycle() {
		this.$interval.cancel(this._cyclerPromise);
		this.fetchMessageText = null;
	}

	showInsightBriefChoices() {
		if (this.modal) {
			// If the story IDs for this post haven't yet been looked up, get them now. (But avoid re-querying...)
			if (this.storyIds === null) {
				this.Api.get(`/v2/posts/${this.post.id}/stories`).then(storyIds => {
					this.storyIds = ensure.array(storyIds).map(i => Number.parseInt(i));
				});
			}

			let url = `${window.BASE_URL}/core/post/${parseInt(this.post.id) || 0}/add-to-insight-brief`;

			if (Array.isArray(this.storyIds) && this.storyIds.length > 0) {
				url += '?story_ids=' + this.storyIds.join(',');
			}
			const modal = this.modal.iframe(url, {
				heading: 'Add Post To Insight Briefs',
				width: 'full',
				height: 'full',
				padded: false,
			});

			modal.listen(evt => {
				try {
					const message = JSON.parse(evt.data);

					if (message.action === 'addPostToStory') {
						if (message.checked === false) {
							spliceOut(this.storyIds, message.storyId);
						} else {
							this.storyIds.push(message.storyId);
						}
					}
				} catch (ex) {
					// NOTE: It's terribly sad if we come here, but there's nothing we're going to do about it.
				}
			});
		}
	}

	saveNew() {
		let data = {
			// members can submit only to curate tab
			publish: this.Identity.can('submit_to_intel_tab'),
			// client posts automatically get the "client view" flag
			client_view: !this.Identity.can('standard_intel') || !!this.post.is_client_intel,
			// medium based on situation
			submission_medium: window.isBrowserPlugin ? 'screenshot' : this.mode == 'bookmarklet' ? 'bookmarklet' : 'intel',
		};
		if (this.post.published_by && this.post.published_by.id) {
			data.published_by_id = this.post.published_by.id;
		}
		if (this.post.submitted_by && this.post.submitted_by.id) {
			data.submitted_by_id = this.post.submitted_by.id;
		}
		const scalarFields = [
			'intel_text',
			'headline',
			'rich_text_body',
			'full_text',
			'summary',
			'source_link',
			'source_name',
			'source_author',
			'approval_status',
			'published_at',
			'source_date',
		];
		scalarFields.forEach(field => {
			if (this.post[field]) {
				data[field] = String(this.post[field]) || undefined;
			}
		});
		data.tags = ensure.array(this.post.tags).map(t => t.name);
		data.category = ensure.number(this.post.category.id) || 0;
		data.classification_id = this.classification ? this.classification.id : undefined;
		data.archive_selection = this.post.archive_selection ? this.post.archive_selection : undefined;
		data.budget = this.post.budget ? this.post.budget.amount : undefined;

		if (this.post.archive_selection) {
			const archiveData = this.AutoArchive.getDateAndActionFromArchiveSelection(this.post.archive_selection);
			data.archive_action = archiveData.archive_action;
			data.archive_at = archiveData.archive_at;
		}

		data.notify_user_ids = this.mentionable.notifications.map(user => user.id);

		if (this.image) {
			// + ADD IMAGE
			data.image = this.image.url;
		}
		if (this.imageChoices && this.imageChoices.length) {
			// Bookmarklet
			const chosen = this.imageChoices[this.imageChosenIdx];
			data.image = chosen ? chosen.src : undefined;
		}
		if (Array.isArray(this.keywords) && this.keywords.length) {
			data.keywords = this.keywords;
		}
		if (this.isResearchManagementHub) {
			data.type_id = PostTypes.research_brief;
		} else {
			data.type_id = PostTypes.post;
		}

		data.access_rule_id = this.post.access_rule_id;

		// create it!
		data = this.augmentSaveData(data);
		this.isSaving = true;
		this.isPublished = false;
		return this.Api.request('POST', '/posts', data).then(res => {
			if (res.status > 399) {
				return Promise.reject(JSON.parse(res.headers('api-response-errors') || ''));
			} else {
				// all good continue on
				const postId = parseInt(res.headers('API-New-Record-Id'), 10);
				this.isPublished = !!parseInt(res.headers('API-Published-Status'));
				this.saveCountdown = 500;
				let dependentSaves = [];
				let attachmentSaves = [];
				this.attachments.forEach(att => {
					// id is numeric for existing attachments and href is the s3 location for new attachments
					const attData = {
						attachment_id: att.id,
						data: att.href || undefined,
						name: att.name,
						size: att.size,
						created_at: att.date,
					};
					// ones with attachment id are saved quickly because they are connected but not downloaded
					// big ones save quickly because they are downloaded in a forked process
					this.saveCountdown += attData.attachment_id || att.size > 10 * 1024 * 1024 ? 400 : 4000;
					attachmentSaves.push(
						this.Api.create(`/posts/${postId}/attachments`, attData).then(newId => {
							return newId || att.id;
						})
					);
				});
				if (this.attachments.length > 0) {
					// After all the attachments save, ensure they get put into the desired order
					dependentSaves.push(
						Promise.all(attachmentSaves).then(newIds => {
							this.Api.put(`/posts/${postId}/attachments/sort`, { ids: newIds });
						})
					);
				}
				if (this.awaitingInsightPost) {
					// delete original post from awaiting insight column
					dependentSaves.push(this.Api.delete(`/posts/${this.awaitingInsightPost}`));
				}
				if (Object.keys(this.updatedTaxonomies).length > 0) {
					// `this.updatedTaxonomies is an object with arrays of numbers as values
					// Turn all the arrays into a single array
					let selectedTaxonomies = [].concat.apply([], Object.values(this.updatedTaxonomies));
					dependentSaves.push(
						this.Api.patch(`/posts/${postId}/taxonomies`, {
							selected_option_ids: selectedTaxonomies,
						})
					);
				}

				// ~~~ handle research project approvers ~~~ \\
				const researchApproverPromises = ensure.array(this.post?.approvers).map(p => [
					this.Api.post(`/posts/${postId}/contributors`, {
						user_id: p.user_id,
						contributor_duty_id: p.contributor_duty_id,
					}),
					this.Api.post('/emails/send', {
						to_recipients: [p.email],
						subject: p.subject,
						message: p.message,
						post_ids: [postId],
					}),
				]);

				researchApproverPromises.forEach(promises => {
					dependentSaves = [...dependentSaves, ...promises];
				});

				return this.$q
					.all(dependentSaves)
					.then(
						() => {},
						err => {
							this.isSaving = false;

							throw err;
						}
					)
					.then(() => postId);
			}
		});
	}

	augmentSaveData(data) {
		// override this in child class to make pre-save updates to data
		return data;
	}

	handleErrors(errors) {
		let errorMessages = '';
		spinner.stopAll();

		if (Array.isArray(errors)) {
			errorMessages = errors.map(e => ensure.string(e.message).replace('Parameter ', '')).join('\n');
		} else {
			errorMessages = 'The server ran into an unexpected error';
		}
		this._alert(`Unable to save post.\n\n${errorMessages}`);
	}

	tagCount() {
		return ensure.array(this.post.tags).filter(t => !t.small_image).length;
	}

	vtagCount() {
		return ensure.array(this.post.tags).filter(t => t.small_image).length;
	}

	budgetChanged() {
		// if you add a value to an input of type number and then remove it, angular will set the backing
		// model to null, which causes an error when the API processes the post
		if (this.post && this.post.budget && (!this.post.budget.amount || this.post.budget.amount < 0)) {
			this.post.budget.amount = undefined;
		}
	}

	validatePost(post) {
		let errors = this.maybeGetMissingFieldErrors(post);

		if (
			this.isResearchManagementHub &&
			_.isObject(post.budget) &&
			(!post.budget.amount || Number(post.budget.amount) < 0)
		) {
			errors.push('Budget amount must be a positive number');
		}

		return { success: errors.length === 0, errors };
	}

	maybeGetMissingFieldErrors(post) {
			const requiredFields = (this.Cfg.read('SharpIt.required_fields') || 'headline').split(',');
			const tagsRegex = /^tags\((\d+)\)$/i;
			const tagCount = ensure.array(post.tags).filter(t => !t.small_image).length;
			const stringFields = ['intel_text', 'headline', 'rich_text_body'];
			const objectFields = ['classification', 'category'];

			const isEmptyString = field => ensure.string(post[field]).trim().length === 0;
			const isMissingId = field => _.isObject(post[field]) && !post[field].id;
			const isInvalidString = field => stringFields.includes(field) && isEmptyString(field);
			const isInvalidObject = field => {
				if (field === 'classification') {
					return objectFields.includes(field) && !_.isObject(this.classification);
				}
				return objectFields.includes(field) && isMissingId(field);
			};

			// Make sure headline is always included. because the api requires it.
			if (!requiredFields.includes('headline')) {
				requiredFields.push('headline');
			}

			let missingFields = requiredFields.filter(field => {
				const [__, requiredTags] = field.match(tagsRegex) || [];
				const minimumNumberOfTags = requiredTags || 0;

				return isInvalidString(field) || isInvalidObject(field) || tagCount < minimumNumberOfTags;
			});

			if (this.isAccessRuleRequired() && !post.access_rule?.id) {
				requiredFields.push('access_rule_id');
				missingFields.push('access_rule_id');
			}

			// Taxonomies are handled differently b/c the taxonomy records themselves tell whether they are required
			// instead of using a preference
			const selectedOptions = {...this.originalTaxonomies, ...this.updatedTaxonomies};
			Object.values(this.taxonomies).forEach(taxonomy => {
				const updatedItem = selectedOptions[taxonomy.name];
				if (
					taxonomy.is_required === true &&
					(updatedItem === undefined ||
						// taxonomy type is autosuggest, and no items is selected
						updatedItem.length === 0 ||
						// taxonomy type is select, and the empty option is selected
						(updatedItem.length === 1 && updatedItem[0] === null))
				) {
					requiredFields.push(`taxonomyField-${taxonomy.name}`);
					missingFields.push(`taxonomyField-${taxonomy.name}`);
				}
			});

			const missingFieldErrors = this._buildRequiredMessage({
				requiredFields,
				missingRequiredFields: missingFields,
			});

			return missingFieldErrors;
	}

	_buildRequiredMessage({ requiredFields, missingRequiredFields }) {
		const visualTagsLabel = this.Cfg.read('Features.visual_tags_label') || 'Visual Tags';
		const classificationLabel = this.isResearchManagementHub ? 'Status' : 'Classification';

		const possibleRequiredFields = {
			access_rule_id: { value: 'access_rule_id', text: 'Access Rule' },
			headline: { value: 'headline', text: 'Headline' },
			category: { value: 'category', text: 'Category' },
			rich_text_body: { value: 'rich_text_body', text: 'Body' },
			intel_text: { value: 'intel_text', text: 'Insight Text' },
			classification: { value: 'classification', text: 'Classification' },
			'tags(1)': { value: 'tags(1)', text: '1 or more tags' },
			'visual_tags(1)': { value: 'visual_tags(1)', text: '1 or more visual tags' },
		};

		const requiredFieldsMapping = ensure
			.array(requiredFields)
			.filter(fieldName => possibleRequiredFields[fieldName])
			.map(fieldName => possibleRequiredFields[fieldName]);

		const labels = requiredFieldsMapping || [{ value: 'headline', text: 'Headline' }];
		const labelText = [];

		missingRequiredFields.forEach(field => {
			const taxonomyRegex = /^taxonomyField-(.*)/gi;
			const found = _.findWhere(labels, { value: field });
			const taxonomyMatches = taxonomyRegex.exec(field);
			if (found && found.text) {
				found.text = found.text.replace('Visual Tags', visualTagsLabel);
				found.text = found.text.replace('Classification', classificationLabel);
				labelText.push(found.text);
			} else if (taxonomyMatches) {
				labelText.push(`${this.labels.taxonomies} "${taxonomyMatches[1]}"`);
			}
		});

		return labelText.map(label => `${label} is required.`);
	}

	getSectionClass(forSection, idx) {
		const classes = {
			'edit-section-left': false,
			'edit-section-center': false,
			'edit-section-right': false,
		};
		if (this.section == forSection) {
			classes['edit-section-center'] = true;
		} else if (forSection == 'main' && this.section != 'main') {
			classes['edit-section-right'] = true;
		} else {
			classes['edit-section-left'] = true;
		}
		return classes;
	}

	setPostDataFromAttachmentData(file) {
		// There are instances where people upload multiple files at once. Only process ones that have an attachment id.
		if (this.attachments.filter(att => att.id).length > 1) {
			// TODO: bail if we already have a first file
			return;
		}
		this.maybeSetMetadata(file);
		this.maybeSetHeadline(file);
		this.maybeAwaitRichText(file);
		this.awaitAiKeywords(file);
		this.awaitPreviewImage(file);
		this.maybeSetSourceLink(file);
	}

	/**
	 * Checks hub setting and opens modal for user to select the extracted file metadata properties
	 * @param file
	 */
	maybeSetMetadata(file) {
		try {
			if (this.Cfg.readAsBool('Features.file_metadata')) {
				getMetadata(file).then(properties => {
					// mini modal with checkboxes that ask what properties they want to carry over.
					this.modal
						.state('hub.loggedin.FileMetadataList', {
							params: { properties, fileName: file.name },
							heading: 'Document Metadata',
							width: 500,
							height: 450,
						})
						.then(metadata => {
							this.setPostDataFromMetadata(metadata);
						});
				});
			} else {
				// setting not enabled, post will not be updated with file metadata
				return;
			}
		} catch (e) {
			// Do nothing
		}
	}

	/**
	 * Attempts to set post data with user selected file metadata properties
	 * @param options array of objects that contain the values ready to be consumed by API
	 */
	setPostDataFromMetadata(options) {
		if (options.length === 0 || !Array.isArray(options)) {
			// if there are no options or options somehow isn't an array, return and not set any post data
			return;
		}
		// set post props
		options.map(item => {
			if (item.id === 'headline') {
				const headline = item.value.replace(/\.\w{1,4}$/, '').replace(/[_\s]+/g, ' ');
				this.post.headline = headline;
			} else if (item.id === 'intel_text') {
				this.post.intel_text = item.value;
			} else if (item.id === 'tags') {
				const keywords = item.value.split(/[,;]+/);
				keywords.forEach(tag => {
					if (tag !== '') {
						tag = tag.trim();
					}
					const newTag = {
						name: tag,
						$search: tag,
					};
					this.post.tags.push(newTag);
				});
			} else if (item.id === 'source_date') {
				this.post.source_date = dayjs(item.value).toISOString();
			} else if (item.id === 'published_at') {
				this.post.published_at = dayjs(item.value).toISOString();
			} else if (item.id === 'source_author') {
				this.post.source_author = item.value;
			} else if (item.id === 'submitted_by' && item.value !== 0) {
				this.post.submitted_by = {
					id: item.value,
					$label: item.label,
				};
			} else if (item.id === 'published_by') {
				this.post.published_by = {
					id: item.value,
					$label: item.label,
				};
			} else {
				// do nothing to post
				return;
			}
		});
	}

	/**
	 * Set post.source_link to the external location of the file (e.g., Google Drive)
	 * @param file
	 */
	maybeSetSourceLink(file) {
		if (file.sourceLink) {
			this.post.source_link = file.sourceLink;
		}
	}

	awaitAiKeywords(file) {
		this.deregisterListeners.aiKeywords = this.Identity.onHubMessage('file.has-text', msg => {
			// other messages use msg.key, but this one uses filename
			if (file.s3Basename === msg.filename) {
				if (msg.aiKeywords && Array.isArray(msg.aiKeywords)) {
					if (!Array.isArray(this.keywords)) {
						this.keywords = [];
					}
					for (const kw of msg.aiKeywords) {
						this.keywords.push({ ...kw, type: 'ai' });
					}
					this.prepareTagTabs();
				}
				if (this.shouldSetRichText(file)) {
					this.fetchingRichText = false;
					this.didFetchSummary = true;
					if (msg.newRichTextBody && !this.hasRichtextContent()) {
						this.post.rich_text_body = msg.newRichTextBody;
					}
					// we don't need to know about other rich text events
					this.deregisterListeners.progress();
				}
				this.$timeout(() => {});
				this.deregisterListeners.aiKeywords();
			}
		});
	}

	shouldSetRichText(file) {
		const setPostBodyTo = this.Cfg.read('DocProcessing.set_post_body');
		// should we fetch the document's summary and populate post body text?
		if (
			// only for create from intel tab
			this.mode !== 'create' ||
			// only for first file
			this.didFetchSummary ||
			// only when account feature is enabled
			setPostBodyTo === 'none' ||
			// we can only extract text from word, pdf and ppt
			['icon-file-word', 'icon-file-pdf', 'icon-file-powerpoint'].indexOf(file.icon) === -1
		) {
			return false;
		}
		return true;
	}

	maybeAwaitRichText(file) {
		if (!this.shouldSetRichText(file)) {
			return;
		}
		this.fetchingRichText = true;
		this.deregisterListeners.progress = this.Identity.onHubMessage('file.progress', msg => {
			if (file.s3Name === msg.key && msg.format === 'json') {
				this.extractTextProgress = msg;
				this.$timeout(() => {});
			}
		});
	}

	maybeSetHeadline(file) {
		if (this.Cfg.readAsBool('AI.generate_post_headline')) {
			generatePostHeadline(file.name).then(aiHeadline => {
				if (!aiHeadline) {
					// do nothing to post, should fall to next if block below
					return;
				}
				this.post.headline = aiHeadline;
				return aiHeadline;
			});
		}
		if (this.Cfg.readAsBool('DocProcessing.set_post_headline') && !this.post.headline && file.name) {
			this.post.headline = file.name.replace(/\.\w{1,4}$/, '').replace(/[_\s]+/g, ' ');
		}
	}

	awaitPreviewImage(file) {
		this.deregisterListeners.previewImage = this.Identity.onHubMessage('file.first-preview', msg => {
			if (file.s3Basename === msg.basename) {
				if (!this.imageChoices || this.imageChoices.length === 0) {
					const geometry = 'fit-235x135';
					this.FileDrag.loadPreviewImage(file, msg, geometry).then(
						image => {
							this.filePreview = image;
							this.$timeout(() => {});
						},
						error => {
							console.log('FileDrag error on fetching preview image:', error);
						}
					);
				}
				this.deregisterListeners.previewImage(); // deregister right away
			}
		});
	}

	hasRichtextContent() {
		return stripTags(this.post.rich_text_body).trim().length > 0;
	}

	onExit() {
		// deregister hub message listeners if modal was closed
		_.forEach(this.deregisterListeners, deregister => deregister());
	}

	_getPostImageByFile(file) {
		const pngs = {
			'icon-file-word': 'doc.png',
			'icon-file-pdf': 'pdf.png',
			'icon-file-powerpoint': 'ppt.png',
		};
		const url = `${window.BASE_URL}/assets/img/filetypes/secondary/${pngs[file.icon]}`;
		// may be used as this.image or this.imageChoices
		return {
			url: url,
			width: 260,
			height: 195,
		};
	}

	removeAiTag(keyword) {
		spliceOut(this.keywords, keyword);
		spliceOut(this.aiTags, keyword);
		spliceOut(this.vocabTags, keyword);
	}

	checkDupesBeforeCreate(file) {
		// shows modal
		if (file.duplicates) {
			this.filesWithDuplicates.push(file);
		} else {
			this.setPostDataFromAttachmentData(file);
		}
	}

	/*
	 * Uploads the file, ignoring duplicates
	 * @param file Object  plupload file object with other attributes added
	 * 					   with some extra attributes
	 */
	uploadDuplicate(uploadedFileIdx, file) {
		// Options:
		// 1) Upload file for reprocessing
		// 2) Create a copy of the attachment <<< current choice
		// 3) Attach file to a second post

		// remove from the list in the modal that shows duplicates
		this.filesWithDuplicates.splice(uploadedFileIdx, 1);
		// create the attachment record
		return this.Api.post(`/attachments`, {
			cloud_file_bucket: file.cloud_file_bucket,
			cloud_file_key: file.s3Name,
			name: file.name,
			size: file.size,
			sha256: file.sha256,
			allow_duplicates: true,
			transcribe: file.transcribe || false,
		}).then(report => {
			// the attachment object doesn't get an id when originally added (because no db record
			/// was created). add the id now that we have it.
			file.listItem.id = report.attachment_id;
			// `attId` is used by maybeFetchSummary
			file.attId = report.attachment_id;
			this.attachments.some((attachment, idx) => {
				if (attachment.href === file.s3Url) {
					this.attachments[idx].id = report.attachment_id;
					return true;
				}
			});
			this.setPostDataFromAttachmentData(file);
		});
	}

	/*
	 * Cancel the upload and remove the file from the duplicates list and from the attachments list
	 * @param file Object  plupload file object with other attributes added
	 * 					   with some extra attributes
	 */
	cancelDuplicate(uploadedFileIdx, file) {
		this.filesWithDuplicates.splice(uploadedFileIdx, 1);
		this.attachments = this.attachments.filter(attachment => attachment.href != file.s3Url);
		// plupload method, otherwise it seems you can't upload the same file
		file.destroy();
		// close all Video Transcription modals
		if (this.modal) {
			this.modal.closeAll('Video Transcription');
		}
	}

	/**
	 * Keeps track of changes to taxonomy selections and adds companion tags if they exist.
	 * @param taxonomy Object  Details about the taxonomy whose options are being updated
	 * @param selectedOptionIds Number[]  An array of taxonomy_options.id values representing the selected taxonomy_options.
	 * @param selectedOption Object|null  details about the `taxonomy_options` item selected for this taxonomy. `null` if the user chose the empty <option>
	 */
	taxonomyUpdated(taxonomy, selectedOptionIds, selectedOption) {
		if (selectedOptionIds.indexOf(null) > -1) {
			this.updatedTaxonomies[taxonomy.name] = [];
		} else {
			this.updatedTaxonomies[taxonomy.name] = selectedOptionIds;
		}
		// some taxonomy options have companion tags that should be applied when the option is chosen. See taxonomy_options.also_tag_with.
		if (selectedOption && selectedOption.also_tag_with) {
			// don't add the tag if it's already there
			const hasTag = this.post.tags.some(tag => tag.name === selectedOption.also_tag_with);
			if (hasTag === false) {
				this.newTagName = selectedOption.also_tag_with;
				this.prepareTagTabs();
				this.addManualTag();
			}
		}
	}

	/**
	 * This function's purpose is to update the post anytime a user selects an image or even uploads
	 * their own from the "Image" tab in the bookmarklet.
	 * Pushes the newly uploaded or selected image to the front of the carousel.
	 */
	runImageUpdate() {
		if (this.section === 'image' && this.mode === 'bookmarklet') {
			if (!this.image) {
				return (this.imageChoices.length = 0);
			}
			this.imageChoices.unshift({
				src: this.image.url,
				width: this.image.width,
				height: this.image.height,
			});
			this.imageChosenIdx = 0;
		}
	}

	isAccessRulesFeatureEnabled() {
		return this.Cfg.readAsBool('Features.access_rules');
	}

	isAccessRuleRequired() {
		return this.isAccessRulesFeatureEnabled() && this.Cfg.readAsBool('Features.access_rules_required');
	}

	loadAccessRule(ruleId) {
		return this.accessRules.find(r => r.id === ruleId);
	}

	validate(post) {
		return this.validatePost(post || this.post);
	}

	showErrorsModal(errors) {
		this._alert(`Unable to save post.\n\n${errors.join('\n')}`);
	}

	updateResearchApprovers = approvers => {
		this.post.approvers = approvers;
	};
}
